import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, FileCheck2, ShieldCheck, CheckCircle2, XCircle, Clock, AlertTriangle,
  FileText, Smartphone, KeyRound,
} from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-2xl">
      <div className="flex items-center gap-2 mb-4 justify-center">
        <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><FileCheck2 className="h-5 w-5" /></div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-500">Assietica</p>
          <p className="font-heading font-semibold text-slate-800">Conferma dei consensi</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="consenso-public-card">
        {children}
      </div>
      <p className="text-center text-xs text-slate-400 mt-4">© Assietica — Raccolta e conferma consensi</p>
    </div>
  </div>
);

const StatusMsg = ({ icon: Icon, tone, title, text }) => (
  <div className="text-center py-6" data-testid="consenso-public-status">
    <div className={`h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 ${tone}`}>
      <Icon className="h-7 w-7" />
    </div>
    <h2 className="font-heading text-xl font-semibold text-slate-800">{title}</h2>
    {text && <p className="text-slate-500 mt-2">{text}</p>}
  </div>
);

export default function SignConsentPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState("");
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [done, setDone] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sign/public/consent/${token}`);
      setData(data);
      setPhoneMasked(data.phone_masked || "");
      setOtpSent(!!data.otp_sent);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Link non valido o scaduto");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setResp = (key, val) => setResponses((prev) => ({ ...prev, [key]: val }));

  const allAnswered = () => (data?.items || []).every((i) => responses[i.key] === "acconsento" || responses[i.key] === "nego");
  const requiredOk = () => (data?.items || []).every((i) => !i.required || responses[i.key] === "acconsento");

  const requestOtp = async () => {
    if (!fullName.trim()) { setErr("Inserisci nome e cognome prima di richiedere il codice"); return; }
    setSendingOtp(true);
    try {
      const { data: r } = await api.post(`/sign/public/consent/${token}/request-otp`);
      setOtpSent(true);
      setPhoneMasked(r.phone_masked || "");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSendingOtp(false); }
  };

  const confirm = async () => {
    setErr("");
    if (!fullName.trim()) { setErr("Inserisci nome e cognome"); return; }
    if (!allAnswered()) { setErr("Rispondi a tutte le voci"); return; }
    if (!requiredOk()) { setErr("Devi accettare le voci obbligatorie per procedere"); return; }
    if (!otpCode.trim()) { setErr("Inserisci il codice OTP ricevuto via SMS"); return; }
    setBusy(true);
    try {
      await api.post(`/sign/public/consent/${token}/confirm`, {
        full_name: fullName.trim(), responses, otp_code: otpCode.trim(),
      });
      setDone(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return <Shell><div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div></Shell>;
  if (err && !data) return <Shell><StatusMsg icon={AlertTriangle} tone="bg-red-100 text-red-600" title="Link non valido" text={err} /></Shell>;
  if (done || data?.status === "CONFERMATO") return <Shell><StatusMsg icon={CheckCircle2} tone="bg-emerald-100 text-emerald-600" title="Consensi confermati" text="Grazie. Le tue risposte sono state registrate. Riceverai una ricevuta via email." /></Shell>;
  if (data?.status === "SCADUTO") return <Shell><StatusMsg icon={Clock} tone="bg-orange-100 text-orange-600" title="Link scaduto" text="Questo link non è più valido. Contatta il tuo intermediario." /></Shell>;
  if (data?.status === "ANNULLATO") return <Shell><StatusMsg icon={XCircle} tone="bg-red-100 text-red-600" title="Richiesta annullata" text="Questa richiesta è stata annullata." /></Shell>;

  return (
    <Shell>
      <div className="space-y-5" data-testid="consenso-public-form">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-800">{data?.title}</h2>
          <p className="text-sm text-slate-500 mt-1">Gentile {data?.signer_name}, prendi visione e conferma i consensi qui sotto.</p>
        </div>

        {data?.intro_text && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line" data-testid="consenso-public-intro">
            {data.intro_text}
          </div>
        )}

        {data?.has_informativa && (
          <a href={`${API}/sign/public/consent/${token}/informativa`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="consenso-public-informativa">
            <FileText className="h-4 w-4" /> Apri l'informativa (PDF)
          </a>
        )}

        <div className="space-y-2" data-testid="consenso-public-items">
          {(data?.items || []).map((it) => (
            <div key={it.key} className="rounded-lg border border-slate-200 p-3" data-testid={`consenso-public-item-${it.key}`}>
              <p className="text-sm text-slate-800">{it.label} {it.required && <span className="text-red-500">*</span>}</p>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => setResp(it.key, "acconsento")} className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${responses[it.key] === "acconsento" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 hover:border-slate-300 text-slate-600"}`} data-testid={`consenso-public-yes-${it.key}`}>
                  Acconsento
                </button>
                <button type="button" onClick={() => setResp(it.key, "nego")} className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${responses[it.key] === "nego" ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 hover:border-slate-300 text-slate-600"}`} data-testid={`consenso-public-no-${it.key}`}>
                  Nego
                </button>
              </div>
            </div>
          ))}
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Nome e cognome</label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Il tuo nome e cognome" data-testid="consenso-public-name" />
        </div>

        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 text-sm text-slate-700 mb-2"><Smartphone className="h-4 w-4 text-sky-600" /> Conferma con codice OTP</div>
          {!otpSent ? (
            <Button onClick={requestOtp} disabled={sendingOtp} variant="outline" className="w-full" data-testid="consenso-public-request-otp">
              {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />} Ricevi il codice via SMS
            </Button>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2">Codice inviato al numero {phoneMasked || "registrato"}.</p>
              <div className="flex gap-2">
                <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Codice OTP" className="font-mono" data-testid="consenso-public-otp" />
                <Button variant="outline" onClick={requestOtp} disabled={sendingOtp} data-testid="consenso-public-resend-otp">
                  {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reinvia"}
                </Button>
              </div>
            </>
          )}
        </div>

        {err && <p className="text-sm text-red-600" data-testid="consenso-public-error">{err}</p>}

        <Button onClick={confirm} disabled={busy || !otpSent} className="w-full bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="consenso-public-confirm">
          {busy ? <Loader2 className="h-5 w-5 animate-spin mr-1.5" /> : <ShieldCheck className="h-5 w-5 mr-1.5" />} Conferma i consensi
        </Button>

        <p className="text-[11px] text-slate-400 text-center">
          Strumento leggero di raccolta consensi con evidenze tecniche. Non costituisce Firma Elettronica Avanzata/Qualificata.
        </p>
      </div>
    </Shell>
  );
}
